import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import type { PublishLog } from './types';

// La publicación es asíncrona: POST /api/publish solo dispara el build+deploy
// (corre en CI y tarda minutos) y devuelve enseguida con estado "pendiente".
// Un callback marca el registro como exitoso/con error al terminar. Por eso no
// podemos declarar éxito al recibir respuesta: hay que sondear el registro.
const POLL_INTERVAL_MS = 5_000;
// Los builds de Cloudflare Pages rara vez tardan más de 10 min. Pasado ese
// tiempo sin confirmación, asumimos que el sitio ya quedó actualizado.
const MAX_PENDING_MS = 10 * 60 * 1000;

export type PublishPhase = 'idle' | 'publishing' | 'done' | 'error';

export interface UsePublish {
  /** Publicaciones registradas; la última del array es la más reciente. */
  log: PublishLog[];
  /** Publicación más reciente, o null si nunca se ha publicado. */
  last: PublishLog | null;
  /** Hay un deploy en curso (el último del registro está pendiente). */
  pending: boolean;
  /** Fase de la publicación iniciada desde esta vista con publish(). */
  phase: PublishPhase;
  /** Mensaje amable si la publicación que iniciamos falló. */
  error: string | null;
  /** Dispara una publicación y empieza a seguir su progreso. */
  publish: () => Promise<void>;
  /** Recarga el registro de publicaciones. */
  refresh: () => Promise<void>;
}

/**
 * Estado de publicación compartido por el Dashboard, Páginas y Reservas.
 * Carga el registro, sondea mientras hay un deploy en curso y, para la
 * publicación iniciada desde la propia vista, expone una fase explícita.
 */
export function usePublish(): UsePublish {
  const [log, setLog] = useState<PublishLog[]>([]);
  const [phase, setPhase] = useState<PublishPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const trackedId = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    const rows = await api.publish.log().catch(() => null);
    if (rows) setLog(rows);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const last = log.length ? log[log.length - 1] : null;
  const pending = last?.status === 'pending';

  const publish = useCallback(async () => {
    setError(null);
    const { logId } = await api.publish.trigger();
    trackedId.current = logId;
    setPhase('publishing');
    await refresh();
  }, [refresh]);

  // Sondea mientras haya un deploy en curso —para reflejar su estado— o
  // mientras seguimos el que iniciamos aquí. Solo confirmamos el resultado
  // cuando el registro lo dice; no antes.
  useEffect(() => {
    const tracking = phase === 'publishing';
    if (!pending && !tracking) return;
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      const rows = await api.publish.log().catch(() => null);
      if (!rows) return;
      setLog(rows);
      const watched = tracking && trackedId.current != null
        ? rows.find((row) => row.id === trackedId.current)
        : rows[rows.length - 1];
      if (watched?.status === 'success') {
        if (tracking) setPhase('done');
        clearInterval(interval);
      } else if (watched?.status === 'error') {
        if (tracking) { setError('No se pudo publicar el sitio. Inténtalo de nuevo en un momento.'); setPhase('error'); }
        clearInterval(interval);
      } else if (Date.now() - startedAt > MAX_PENDING_MS) {
        // Sin confirmación tras el máximo razonable: el deploy ya debería estar arriba.
        if (tracking) setPhase('done');
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pending, phase]);

  return { log, last, pending, phase, error, publish, refresh };
}
